import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Spinner } from "./Spinner";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "inverse";
type Size = "sm" | "md" | "lg";

/*
 * Primary carries a faint top highlight and a contact shadow so a flat brand
 * colour still reads as a raised, pressable key. Every colour is a token, so
 * an org's brand reaches every button with no per-screen work.
 */
const VARIANT: Record<Variant, string> = {
  primary:
    "bg-brand text-on-brand shadow-[inset_0_1px_0_rgb(255_255_255/0.14),0_1px_2px_rgb(0_0_0/0.14)] " +
    "hover:bg-brand-hover active:bg-brand-active",
  secondary:
    "bg-raised text-ink border border-rule shadow-control " +
    "hover:border-brand-border hover:text-brand-ink active:bg-brand-light",
  ghost: "bg-transparent text-ink hover:bg-brand-light hover:text-brand-ink",
  /** For the dark panel surfaces (sign-in panel, dashboard band). */
  inverse:
    "bg-panel-text/10 text-panel-text border border-panel-text/20 " +
    "hover:bg-panel-text/15 hover:border-panel-text/35 active:bg-panel-text/20",
  danger:
    "bg-danger text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.14),0_1px_2px_rgb(0_0_0/0.14)] " +
    "hover:brightness-95 active:brightness-90",
};

// Radius lives with the size so a large touch target can round more without a
// className override fighting the base classes.
const SIZE: Record<Size, string> = {
  sm: "h-8 px-3 text-[13px] gap-1.5 rounded-md",
  md: "h-10 px-4 text-sm gap-2 rounded-md",
  /** Thumb-sized: the karigar app's primary actions. */
  lg: "h-14 px-6 text-base gap-2.5 rounded-lg",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
  block?: boolean;
}

export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  icon,
  block = false,
  disabled,
  className = "",
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      className={[
        "inline-flex shrink-0 items-center justify-center font-title font-semibold whitespace-nowrap",
        "transition-[background-color,border-color,color,box-shadow,translate] duration-150",
        "active:translate-y-px disabled:pointer-events-none disabled:opacity-55 disabled:shadow-none",
        VARIANT[variant],
        SIZE[size],
        block ? "w-full" : "",
        className,
      ].join(" ")}
      // `||`, not `??`: an explicit disabled={false} must not re-enable a
      // button mid-request, or a double-tap submits twice.
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? <Spinner /> : icon}
      {children}
    </button>
  );
}
